import { ModalWrapper } from '@hooks/useModal';
import { QuestionMarkOutlined } from '@mui/icons-material';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { memo, useEffect, useState } from 'react';
import { useLazyGetProfileQuery } from '../../../../api/front-office/profile.api.rtk';
import DataTable from '../../../../components/table/datatable';
import useConfirmationDialog from '../../../../hooks/useConfirmationDialog';
import useDisclosure from '../../../../hooks/useDisclosure';
import useInformationDialog from '../../../../hooks/useInformationDialog';
import useToast from '../../../../hooks/useToast';
import { ProfileDetailsList } from '../../interface/fo-profile-interface';

const profileIndividualColumns: GridColDef[] = [
  {
    field: 'caption',
    headerName: 'Name',
    minWidth: 150,
    flex: 1.5,
    hideable: false,
  },

  {
    field: 'tinNo',
    headerName: 'Tin No.',
    minWidth: 100,
    flex: 1.5,
    hideable: false,
    sortable: false,
  },
  {
    field: 'address',
    headerName: 'Address',
    minWidth: 200,
    flex: 1,
    hideable: false,
    align: 'left',
    sortable: false,
  },
];

const visibleColumns = profileIndividualColumns.filter(
  (column) =>
    !['propertyID', 'propertyName', 'profileID'].includes(column.field),
);

const FOGuestListTable = ({
  isOpen,
  onClose,
  onAccount,
  postCityLedger,
  totalPayables,
  transactionType = 'City Ledger',
}: {
  isOpen: boolean;
  onClose: () => void;
  onAccount: (
    profileId: string,
    profileName: string,
    creditLimit: number,
    profileTin: string,
  ) => void;
  postCityLedger?: () => void;
  totalPayables: number;
  transactionType?: string;
}) => {
  const { toast } = useToast();

  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [selectedProfileName, setProfileName] = useState<string>('');
  const [selectedCreditLimit, setCreditLimit] = useState<number>(0);
  const [selectedProfileTin, setProfileTin] = useState<string>('');

  const {
    DialogComponentV2: ConfirmDialog,
    DialogComponentV2: CreditLimitDialog,
  } = useConfirmationDialog();

  const { DialogInformation, onShowInfoDialog } = useInformationDialog();

  const confirmDialogDisclosure = useDisclosure();
  const creditLimitDisclosure = useDisclosure();

  const [profileData, setProfileDataTable] = useState<ProfileDetailsList[]>(
    [] as ProfileDetailsList[],
  );

  const [getProfileList, { isLoading }] = useLazyGetProfileQuery();

  useEffect(() => {
    getProfileIndividualListFunction();
  }, [getProfileList]);

  const getProfileIndividualListFunction = async () => {
    try {
      const response = await getProfileList(``).unwrap();
      const profiles = response.data.profiles;
      const profileDetailedList = profiles
        .map((profile) => profile.profiles)
        //.filter((profile) => profile.isCLHotel === true);

        .filter((profile) => {
          //add filter for accounts with CL Privs for Hotel
          if (transactionType !== 'City Ledger') {
            return true;
          }

          return profile.isCLHotel === true;
        });

      setProfileDataTable(profileDetailedList);
    } catch (error) {
      console.error('Failed to fetch profile individual list:', error);
    }
  };

  const handleCreditLimitValidation = () => {
    if (selectedProfileId || selectedProfileName) {
      const parsedCreditLimit = parseFloat(
        String(selectedCreditLimit).replace(/,/g, ''),
      );

      if (totalPayables > parsedCreditLimit) {
        creditLimitDisclosure.onOpen();
      } else {
        handleOnClickSelect();
      }
    } else {
      toast({
        severity: 'warning',
        message: 'Selected CL Account is invalid or no account is selected.',
      });
      return;
    }
  };
  const handleSelect = () => {
    if (transactionType === 'City Ledger') {
      confirmDialogDisclosure.onOpen();
    } else {
      if (selectedProfileId && selectedProfileName) {
        if (selectedProfileTin?.length === 0) {
          onShowInfoDialog({
            title: 'No TIN Saved',
            desc: 'Please go to Profile Management and provide a \nTIN of the guest/company first.',
          });
          return;
        }
        onAccount(
          selectedProfileId,
          selectedProfileName,
          selectedCreditLimit,
          selectedProfileTin,
        );

        // Close the modal
        onClose();
      } else {
        onShowInfoDialog({
          desc: 'Please select a profile.',
        });
      }
    }
  };

  const handleOnClickSelect = () => {
    onAccount(
      selectedProfileId,
      selectedProfileName,
      selectedCreditLimit,
      selectedProfileTin,
    );
    // Perform any additional actions, like posting to City Ledger
    if (postCityLedger) {
      postCityLedger();
    }

    // Close the modal
    onClose();
  };

  return (
    <>
      <ModalWrapper
        {...{
          modalDisclosure: {
            isOpen,
            onClose,
            onToggle: () => {},
            onOpen: () => {},
          },
          withHeader: {
            title: 'Select Profile',
            onClose,
          },
          sx: { '.with-header-main': { p: 0, gap: 0 } },
        }}
      >
        <Stack component='form' id='pre-approve-city-ledger'>
          <Stack sx={{ p: 3 }}>
            <DataTable
              getRowId={(row) => row.profileID}
              columns={visibleColumns}
              rows={profileData || []}
              checkboxSelection={false}
              onRowSelectionModelChange={(newSelection) => {
                const selectedRow = profileData.find(
                  (row) => row.profileID === newSelection[0],
                );
                setSelectedProfileId(selectedRow ? selectedRow.profileID : '');
                setProfileName(selectedRow ? selectedRow.caption : '');
                setCreditLimit(selectedRow ? selectedRow.creditLimit : 0);
                setProfileTin(selectedRow?.tinNo || '');
              }}
              loading={isLoading}
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              pageSize={100}
              autoPageSize={false}
              tableStyle={{
                padding: 0,
                '&.MuiDataGrid-root': {
                  p: 0,
                },
                '& .MuiTablePagination-root': {
                  p: '0 10px',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'offWhite',
                  borderRadius: 0,
                },
                ".MuiButtonBase-root[aria-label='Export']": {
                  display: 'none!important',
                },
              }}
              height={400}
            />
          </Stack>
          <Box>
            <Divider sx={{ borderStyle: 'dashed' }} />
            <Stack direction='row' gap={1} justifyContent='flex-end' p={3}>
              <Button
                sx={{
                  width: '100px',
                  height: '35px',
                  border: '1px solid',
                  backgroundColor: 'transparent',
                }}
                onClick={onClose}
              >
                Close
              </Button>
              <Button
                variant='contained'
                sx={{
                  width: 'auto',
                  height: '35px',
                }}
                onClick={handleSelect}
              >
                Select
              </Button>
            </Stack>
          </Box>
        </Stack>
      </ModalWrapper>

      <ConfirmDialog
        open={confirmDialogDisclosure.isOpen}
        desc={
          <Stack gap='8px' sx={{ minWidth: { sx: 'auto', md: '300px' } }}>
            <Typography>
              You are about to charge this CL to{' '}
              <strong>{selectedProfileName}</strong>
            </Typography>
            <Typography>
              Click <strong>Yes</strong> to proceed or <strong>Cancel</strong>{' '}
              to abort.
            </Typography>
          </Stack>
        }
        title='Confirm Account'
        cancelButtonTitle='Cancel'
        confirmButtonTitle='Yes'
        icon={QuestionMarkOutlined}
        onConfirm={async () => {
          handleCreditLimitValidation();
          confirmDialogDisclosure.onClose();
        }}
        onClose={() => {
          confirmDialogDisclosure.onClose();
        }}
      />
      <CreditLimitDialog
        open={creditLimitDisclosure.isOpen}
        desc={
          <Stack gap='8px' sx={{ minWidth: { sx: 'auto', md: '300px' } }}>
            <Typography>
              The CL amount exceeds the credit limit amount. <br />
            </Typography>
            <Typography>
              Would you like to continue? Click <strong>Yes</strong> to Proceed
              <br /> or <strong>No</strong> to cancel the transaction.
            </Typography>
          </Stack>
        }
        title='Confirm'
        cancelButtonTitle='No'
        confirmButtonTitle='Yes'
        icon={QuestionMarkOutlined}
        onConfirm={async () => {
          handleOnClickSelect();
          creditLimitDisclosure.onClose();
        }}
        onClose={() => {
          creditLimitDisclosure.onClose();
        }}
      />
      <DialogInformation />
    </>
  );
};

export default memo(FOGuestListTable);
